import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, CircularProgress, List, ListItem,
  RadioGroup, FormControlLabel, Radio, TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckIcon from "@material-ui/icons/Check";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  stateList: { padding: 0 },
  stateItem: {
    borderRadius: 8,
    marginBottom: 8,
    border: "1px solid",
    cursor: "pointer",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    "&:hover": { filter: "brightness(0.97)" },
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    flexShrink: 0,
  },
  checkIcon: { marginLeft: "auto", fontSize: 18 },
  content: { paddingTop: 8, minWidth: 320 },
}));

const ContactStateModal = ({ open, onClose, contactId, currentState, states, lostReasons, onStateChanged, changeState, loading, forceStep, hideBack = false }) => {
  const classes = useStyles();
  const [step, setStep] = useState("state"); // "state" | "lost"
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(forceStep || "state");
      // If jumping to lost step, pre-select the Perdido state
      if (forceStep === "lost") {
        const perdidoState = states.find(s => s.systemId === 5);
        if (perdidoState) setSelectedStateId(perdidoState.id);
      } else {
        setSelectedStateId(null);
      }
      setSelectedReasonId("");
      setOtherReason("");
    }
  }, [open, forceStep]);

  const handleSelectState = async (state) => {
    // systemId=5 (Perdido) → show lost reasons step
    if (state.systemId === 5) {
      setSelectedStateId(state.id);
      setStep("lost");
      return;
    }
    // All other states → confirm immediately
    setSaving(true);
    try {
      const newState = await changeState(state.id);
      onStateChanged(newState || state);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmLost = async () => {
    setSaving(true);
    try {
      const lostReasonId = selectedReasonId && selectedReasonId !== "other"
        ? Number(selectedReasonId)
        : null;
      // feat_395: el texto de "Otro motivo" ahora viaja (antes se perdía).
      const lostReasonText = selectedReasonId === "other" ? otherReason.trim() : "";
      const newState = await changeState(selectedStateId, lostReasonId, { lostReasonText });
      const lostState = states.find(s => s.id === selectedStateId);
      onStateChanged(newState || lostState);
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      {step === "state" && (
        <>
          <DialogTitle>{i18n.t("contactStateModal.title")}</DialogTitle>
          <DialogContent className={classes.content}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <CircularProgress size={28} />
              </div>
            ) : (
              <List className={classes.stateList}>
                {states.map((state) => {
                  const isActive = currentState?.id === state.id;
                  return (
                    <ListItem
                      key={state.id}
                      className={classes.stateItem}
                      style={{
                        borderColor: isActive ? state.color : "#e0e0e0",
                        backgroundColor: isActive ? `${state.color}18` : "transparent",
                      }}
                      onClick={() => !saving && handleSelectState(state)}
                    >
                      <span className={classes.dot} style={{ backgroundColor: state.color }} />
                      <Typography variant="body2" style={{ fontWeight: isActive ? 600 : 400 }}>
                        {state.name}
                      </Typography>
                      {isActive && (
                        <CheckIcon className={classes.checkIcon} style={{ color: state.color }} />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={saving}>{i18n.t("contactStateModal.cancel")}</Button>
          </DialogActions>
        </>
      )}

      {step === "lost" && (
        <>
          <DialogTitle>{i18n.t("contactStateModal.lostTitle")}</DialogTitle>
          <DialogContent className={classes.content}>
            <RadioGroup
              value={selectedReasonId}
              onChange={(e) => setSelectedReasonId(e.target.value)}
            >
              {lostReasons.map((reason) => (
                <FormControlLabel
                  key={reason.id}
                  value={String(reason.id)}
                  control={<Radio size="small" />}
                  label={reason.name}
                />
              ))}
              <FormControlLabel
                value="other"
                control={<Radio size="small" />}
                label={i18n.t("contactStateModal.otherReason")}
              />
            </RadioGroup>
            {selectedReasonId === "other" && (
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={i18n.t("contactStateModal.otherReasonLabel")}
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                style={{ marginTop: 12 }}
                inputProps={{ maxLength: 200 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            {/* feat_395: el Tablero CRM abre directo en motivos (drop en Perdido) y
                "Atrás" no tiene sentido ahí: cancelar = la ficha vuelve. */}
            {hideBack ? (
              <Button onClick={onClose} disabled={saving}>{i18n.t("contactStateModal.cancel")}</Button>
            ) : (
              <Button onClick={() => setStep("state")} disabled={saving}>{i18n.t("contactStateModal.back")}</Button>
            )}
            <Button
              onClick={handleConfirmLost}
              color="primary"
              variant="contained"
              disabled={saving || !selectedReasonId || (selectedReasonId === "other" && !otherReason.trim())}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : i18n.t("contactStateModal.confirm")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ContactStateModal;
